import { Result, Spin } from 'antd';
import { useEffect, useState } from 'react';

const TSUM_API = process.env.REACT_APP_JACSYS_TSUM_API;

// Native Edulogy apps sign in here (spec: .scratch/lunch-order, issue 04):
// the app opens /native-login?redirect_uri=<app scheme> in a system browser
// session; this page runs tsum's own Microsoft login, then hands tsum's
// token back to the app. No secret ships in the app.
const ALLOWED_REDIRECTS = ['edulogy-tuck-shop://', 'edulogy-teacher://'];
const STORAGE_KEY = 'native_login_redirect';
// Long enough for the Microsoft round trip; a stale flag must not hijack a
// later web login.
const PENDING_TTL_MS = 10 * 60 * 1000;

function isAllowed(redirect: string | null): redirect is string {
  return !!redirect && ALLOWED_REDIRECTS.some((prefix) => redirect.startsWith(prefix));
}

// The redirect a native login started with, if one is still in progress.
export function pendingNativeRedirect(): string | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null');
    if (saved && Date.now() - saved.at < PENDING_TTL_MS && isAllowed(saved.redirect)) {
      return saved.redirect;
    }
  } catch {
    // Unreadable storage: treat as no native login in progress.
  }
  return null;
}

const NativeLogin = () => {
  const [error, setError] = useState<string>();

  useEffect(() => {
    const redirect =
      new URLSearchParams(window.location.search).get('redirect_uri') ?? pendingNativeRedirect();
    if (!isAllowed(redirect)) {
      setError('Буруу холбоос.');
      return;
    }

    fetch(`${TSUM_API}/auth/oauth2`, { method: 'POST', credentials: 'include' })
      .then(async (res) => {
        if (res.status === 401) {
          // Not signed in: run tsum's Microsoft login, which returns to "/";
          // the root route sends us back here.
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ redirect, at: Date.now() }));
          window.location.href = `${TSUM_API}/auth/login`;
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { token } = await res.json();
        sessionStorage.removeItem(STORAGE_KEY);
        const separator = redirect.includes('?') ? '&' : '?';
        window.location.replace(`${redirect}${separator}token=${encodeURIComponent(token)}`);
      })
      .catch(() => setError('Нэвтрэхэд алдаа гарлаа. Дахин оролдоно уу.'));
  }, []);

  if (error) return <Result status="error" title={error} />;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
      <Spin size="large" />
    </div>
  );
};

export default NativeLogin;
